/* eslint-disable array-callback-return */
/* eslint-disable @typescript-eslint/no-shadow */
import { IFlightResultItem } from '@/containers/SearchFlightTab/SearchFlightResultItem/index.d';
import { TFilterFlight } from '@/slices/filterFlight/filterFlightSlice';
import { FlightData } from '@/slices/flightData/slices';
import {
  resetGtag,
  setTrackedBaggagePage,
  updateGtagData,
  setTrackedPassengerPage,
  setTrackedPaySuccessPage,
} from '@/slices/gtag/slices/gtagSlice';
import { IPassengerSearchForm } from '@/slices/passenger/slices/type';
import { format } from 'date-fns';
import { gtagEvent } from 'lib/gtag';
import { isEmpty, isEqual } from 'lodash';
import { useRouter } from 'next/router';
import { getMobileOperatingSystem } from '../checkDevice';
import { deepEqual } from '../deepCompare';
import { useAppSelector } from '../hook';
import { getTotalDiscountAmount, getTotalTripPrice } from '../tripMapping';
import { useAppDispatch } from '../useGetBusRouteDataWithFilter';

export type TGtagEvent =
  | 'add_payment_info'
  | 'purchase'
  | 'add_shipping_info'
  | 'begin_checkout';

type TuseGtag = {
  action: TGtagEvent;
};

export const getPriceByPassengerType = (
  passengerType: 'adt' | 'chd' | 'inf',
  flight: FlightData | null,
) => {
  if (!flight) return 0;
  if (passengerType === 'adt') return flight.route.schedules[0].totalPriceAdult;
  if (passengerType === 'chd') return flight.route.schedules[0].totalPriceChild;
  if (passengerType === 'inf') {
    return flight.route.schedules[0].totalPriceInfant;
  }
  return flight.route.schedules[0].totalPriceAdult;
};

export const getItem = ({
  flight,
  from,
  to,
  url,
  quantity,
  lat,
  long,
  routeType,
  selectedSort,
  filter,
  oldAirlines,
  passengerType,
}: {
  flight: FlightData | null;
  from: IFlightResultItem | null;
  to: IFlightResultItem | null;
  url: string;
  long?: string;
  lat?: string;
  quantity: number;
  routeType?: 'depart' | 'return';
  selectedSort: any;
  passengerType: 'adt' | 'chd' | 'inf';
  filter: any;
  oldAirlines: any;
}) => {
  if (!flight) return null;
  const segment = flight.segments[0];
  const { item_list_id, item_list_name } = getItemProductList({
    filter,
    flight,
    from,
    routeType,
    to,
    oldAirlines,
    selectedSort,
  });
  const sku = ((routeType === 'depart' ? from?.id : to?.id) || '')
    + ((routeType === 'return' ? to?.id : from?.id) || '')
    + segment.startPoint
    + segment.flightNumber;

  return {
    item_id: sku,
    affiliation: getAffiliation(),
    item_name: `${flight.company.name} ${
      routeType === 'depart' ? from?.location : to?.location
    } ${routeType === 'depart' ? to?.location : from?.location} ${format(
      new Date(segment.startTime),
      'MM/dd',
    )}`,
    item_brand: flight.company.name,
    item_category:
      routeType === 'depart'
        ? `${from?.airports?.[0].nameVn}-${to?.airports?.[0].nameVn}`
        : `${to?.airports?.[0].nameVn}-${from?.airports?.[0].nameVn}`,
    item_category2: flight.flightNumber,
    item_category3: format(new Date(segment.startTime), 'yyyy-MM-dd'),
    item_category4: flight.time,
    item_category5: segment.class,
    item_category6: 'Flight',
    item_variant: url,
    item_list_id,
    item_list_name,
    location_id: long && lat ? `${long}.${lat}` : null,
    quantity,
    discount: passengerType !== 'inf' ? flight.route.schedules[0]?.discountAmount : 0,
    price: getPriceByPassengerType(passengerType, flight),
  };
};

export const getArrItems = ({
  passenger,
  ...rest
}: {
  flight: FlightData | null;
  from: IFlightResultItem | null;
  to: IFlightResultItem | null;
  url: string;
  long?: string;
  lat?: string;
  selectedSort: any;
  routeType?: 'depart' | 'return';
  passenger: any;
  filter: any;
  oldAirlines: any;
}) => {
  const result = [];
  const ticketAdt = passenger.adt
    && getItem({
      passengerType: 'adt',
      quantity: passenger.adt,
      ...rest,
    });
  let ticketChd;
  if (passenger.chd) {
    if (getPriceByPassengerType('chd', rest?.flight)
  !== getPriceByPassengerType('adt', rest?.flight)) {
      ticketChd = getItem({
        quantity: passenger.chd,
        passengerType: 'chd',
        ...rest,
      });
    } else {
      ticketAdt.quantity = Number(ticketAdt.quantity) + Number(passenger.chd);
    }
  }
  const ticketInf = passenger.inf
    && getItem({
      quantity: passenger.inf,
      passengerType: 'inf',
      ...rest,
    });

  if (ticketAdt) result.push(ticketAdt);
  if (ticketChd) result.push(ticketChd);
  if (ticketInf) result.push(ticketInf);
  return result;
};

const sortObj = {
  departureTime_desc: 'Gio di som nhat',
  departureTime_asc: 'Gio di muon nhat',
  fare_asc: 'Gia tang dan',
  fare_desc: 'Gia giam dan',
  arrivalTime_asc: 'Gio den som nhat',
  arrivalTime_desc: 'Gio den muon nhat',
};

export const airlineMapping = {
  'vietnam-airlines': 'VN',
  'vietjet-air': 'VJ',
  'bamboo-airways': 'QH',
  'vietravel-airlines': 'VU',
  'pacific-airlines': 'BL',
};

const airlineObj = {
  VN: 'Vietnam Airlines',
  BL: 'Pacific Airlines',
  VJ: 'VietJet Air',
  QH: 'Bamboo Airways',
  VU: 'Vietravel Airlines',
};

const serializeArlines = (airlines: string[]) => {
  const mapAirlines = airlines.map((airline) => (airlineObj as any)[airline]);
  return mapAirlines?.join(' ');
};

export const serializeFilter = (
  filter: TFilterFlight,
  oldFlights: string[],
) => {
  const {
    arrivalTime, departureTime, duration, fare, airline,
  } = filter;
  const result = '';
  let mapAirline: any = airline;
  if (!airline) {
    mapAirline = oldFlights;
  }

  const fareFormat = `${((fare?.min || 0) / 1000).toFixed(0)}k - ${
    fare?.max ? `${((fare?.max || 0) / 1000).toFixed(0)}k` : 'Bất kỳ'
  }`;
  return `${departureTime?.min}-${departureTime?.max}|${arrivalTime?.min}-${
    arrivalTime?.max
  }|${Number(duration?.min) / 60} - ${
    Number(duration?.max) / 60
  }|${fareFormat}`;
};

const defaultAirlines = ['VN', 'VJ', 'BL', 'QH', 'VU'];

const defaultFilter: any = {
  arrivalTime: {
    min: '00:00',
    max: '24:00',
  },
  departureTime: {
    min: '00:00',
    max: '24:00',
  },
  duration: {
    min: '0',
    max: '1440',
  },
  fare: {
    min: 0,
  },
};

export const checkIsHaveFilter = (
  filter: TFilterFlight,
  oldAirlines: string[],
) => {
  let copyDefaultFilter = JSON.parse(JSON.stringify(defaultFilter));
  const { page, ...rest } = filter;
  const copyFilter = !rest?.arrivalTime
    ? copyDefaultFilter
    : JSON.parse(JSON.stringify(rest));
  if (!copyFilter?.fare) {
    copyFilter.fare = {
      min: 0,
    };
  }

  if (rest?.airline) {
    copyDefaultFilter = {
      ...copyDefaultFilter,
      airline: rest?.airline,
    };
  }

  const subCheck = deepEqual(copyDefaultFilter, copyFilter);

  if (!rest?.airline) {
    return !subCheck;
  }
  return !(
    subCheck && deepEqual({ airline: rest.airline }, { airline: oldAirlines })
  );
};

export const getItemProductList = ({
  flight,
  filter,
  from,
  to,
  routeType,
  oldAirlines,
  selectedSort,
}: {
  flight: FlightData | null;
  filter: any;
  routeType?: 'depart' | 'return';
  selectedSort: any;
  oldAirlines: string[];
  from: IFlightResultItem | null;
  to: IFlightResultItem | null;
}) => {
  const isHaveFilter = checkIsHaveFilter(filter, oldAirlines);

  const mapFilter = !filter?.arrivalTime ? defaultFilter : filter;

  const sortKey = `${selectedSort?.type}_${selectedSort?.value}`;

  let item_list_name = '';
  if (flight) {
    item_list_name = `May bay|${
      routeType === 'depart' ? from?.stateName : to?.stateName
    }|${routeType === 'depart' ? to?.stateName : from?.stateName}|${format(
      new Date(flight.date),
      'yyyy-MM-dd',
    )}|${(sortObj as any)[sortKey]}|${serializeFilter(mapFilter, oldAirlines)}`;
  }

  return {
    item_list_id: `${from?.id}t${to?.id}_${+isHaveFilter}`,
    item_list_name,
  };
};

export const useGetTotalPriceBaggage = () => {
  const selectedBaggages = useAppSelector(
    (state) => state.baggage.selectedBaggages,
  );
  let departBaggagePrice = 0;
  let returnBaggagePrice = 0;
  departBaggagePrice = (selectedBaggages.depart || []).reduce(
    (total, baggageData) => total + baggageData.baggage.price,
    0,
  );
  returnBaggagePrice = (selectedBaggages.return || []).reduce(
    (total, baggageData) => total + baggageData.baggage.price,
    0,
  );

  const totalBaggagePrice = departBaggagePrice + returnBaggagePrice;
  return { totalBaggagePrice };
};

export const useGetGtagData = () => {
  const { asPath, query } = useRouter();
  const selectedFlight = useAppSelector((state) => state.selectedFlight);
  const { filter, oldAirlines } = useAppSelector((state) => state.filterFlight);
  const { selectedSort } = useAppSelector((state) => state.sortingFlight);
  const { totalBaggagePrice } = useGetTotalPriceBaggage();

  const { gtag } = useAppSelector((state) => state.gtagSlice);

  const flightForm = useAppSelector((state) => state.searchFlightForm);

  const { passenger } = flightForm;

  const totalPriceDepart = getTotalTripPrice(
    selectedFlight.flight,
    flightForm.passenger,
  );
  const totalPriceReturn = selectedFlight?.returnFlight
    ? getTotalTripPrice(selectedFlight.returnFlight, flightForm.passenger)
    : 0;
  const totalDiscountDepart = getTotalDiscountAmount(
    selectedFlight?.flight?.route.schedules[0].discountAmount || 0,
    flightForm?.passenger,
  );
  const totalDiscountReturn = getTotalDiscountAmount(
    selectedFlight?.returnFlight?.route.schedules[0].discountAmount || 0,
    flightForm?.passenger,
  );

  const selectFlight = query?.type === 'return' && selectedFlight?.returnFlight
    ? selectedFlight?.returnFlight
    : selectedFlight.flight;
  if (!selectFlight) return null;

  const items = getArrItems({
    passenger,
    flight: selectFlight,
    from: flightForm.startPointHome,
    to: flightForm.toPointHome,
    selectedSort,
    filter,
    oldAirlines,
    routeType: (query?.type || 'depart') as any,
    url: asPath.split('?')[1],
  });
  const departItems = (query?.type === 'return' ? gtag?.items?.filter((item, index) => index < items.length) : gtag?.items) || [];
  let itemsMapping: any[] = query?.type === 'return' && gtag?.items
    ? [...departItems, ...items]
    : items;
  itemsMapping = itemsMapping.map((item, index: number) => ({
    ...item,
    index,
  }));
  return {
    currency: 'VND',
    value:
      Number(totalPriceDepart)
      + Number(totalPriceReturn)
      - Number(totalDiscountDepart)
      - Number(totalDiscountReturn),
    coupon: null,
    items: itemsMapping,
  };
};

const useGetBaggageData = () => {
  const { depart, return: returnBaggage } = useAppSelector(
    (state) => state.baggage.selectedBaggages,
  );
  let result = '';
  if (!depart || depart.length === 0) return result;
  depart.map((departBaggage: any, index: number) => {
    // let baggageValue = +departBaggage?.baggage.value
    //   .replace('OVER', '')
    //   .replace('BG', '');
    let [baggageValueStr] = ((departBaggage?.baggage.value || []).match(/(\d+)(\s*(kg|KILO))*/gi) || []) as string[];
    if (baggageValueStr) {
      baggageValueStr = baggageValueStr.replace(/(kg|KILO)/i, '');
    }
    // const baggageValue = departBaggage?.baggage.airline !== 'QH'
    //   ? Number(baggageValueStr)
    //   : Number(baggageValueStr) - 20;
    const baggageValue = Number(baggageValueStr);

    result += `baggage_${baggageValue}kg_${departBaggage?.baggage.price}`;
    if (index !== depart.length - 1) result += '|';
  });

  if (returnBaggage && returnBaggage?.length > 0) {
    returnBaggage.map((baggageReturn: any) => {
      // let baggageValueReturn = +baggageReturn?.baggage.value
      //   .replace('OVER', '')
      //   .replace('BG', '');
      let [baggageValueStr] = ((baggageReturn?.baggage.value || []).match(/(\d+)(\s*(kg|KILO))*/gi) || []) as string[];
      if (baggageValueStr) {
        baggageValueStr = baggageValueStr.replace(/(kg|KILO)/i, '');
      }
      // const baggageValueReturn = baggageReturn?.baggage.airline !== 'QH'
      //   ? Number(baggageValueStr)
      //   : Number(baggageValueStr) - 20;
      const baggageValueReturn = Number(baggageValueStr);
      result += `|baggage_${baggageValueReturn}kg_${baggageReturn?.baggage.price}`;
    });
  }

  return result;
};

export const useTrackBaggaegPage = () => {
  const { gtag, isTrackedBaggagePage } = useAppSelector(
    (state) => state.gtagSlice,
  );
  const { totalBaggagePrice } = useGetTotalPriceBaggage();
  const gtagShippingTier = useGetBaggageData();
  const dispatch = useAppDispatch();
  const payload = {
    ...gtag,
    shipping_tier: gtagShippingTier,
  };
  const handleTrack = () => {
    if (isTrackedBaggagePage) return;

    gtagEvent({
      action: 'add_shipping_info',
      data: payload,
    });
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        ...payload,
        event: 'add_shipping_info',
      });
    }
    dispatch(updateGtagData({
      shipping_tier: gtagShippingTier,
      shipping: totalBaggagePrice,
    }));
    dispatch(setTrackedBaggagePage());
  };
  return {
    handleTrack,
  };
};

const paymentObj = {
  cybersource: 'credit_card',
  onepay: 'internet_banking',
  momo: 'momo',
  zalo: 'zalo',
  airpay: 'shopee pay',
  moca: 'moca',
  vnpay: 'vnpay',
};

export const useTrackPayPage = () => {
  const { gtag } = useAppSelector((state) => state.gtagSlice);
  const dispatch = useAppDispatch();
  const handleTrack = ({ payment_gateway }: { payment_gateway: string }) => {
    if (!payment_gateway || !gtag) return;
    const oldPaymentType = gtag?.payment_type;
    const paymentValue = (paymentObj as any)[payment_gateway];
    if (oldPaymentType === paymentValue) return;
    const { payment_type, ...rest } = gtag;
    const payload = {
      ...rest,
      payment_type: paymentValue,
    };
    gtagEvent({
      action: 'add_payment_info',
      data: payload,
    });
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        ...payload,
        event: 'add_payment_info',
      });
    }
    dispatch(updateGtagData({ payment_type: paymentValue }));
  };
  return {
    handleTrack,
  };
};

export const getAffiliation = (utm_source?: string) => {
  const device = getMobileOperatingSystem();
  let result = '';
  if (device === 'desktop') {
    result += 'fe_desktop';
  } else {
    result += 'fe_mobile';
  }
  if (utm_source) {
    result += `_${utm_source}`;
  }
  return result;
};

export const useTrackPaySuccessPage = () => {
  const { gtag, isTrackedPaySuccessPage } = useAppSelector(
    (state) => state.gtagSlice,
  );
  // const { totalBaggagePrice } = useGetTotalPriceBaggage();
  const dispatch = useAppDispatch();
  const handleTrack = ({ orderId }: { orderId: string }) => {
    if (isTrackedPaySuccessPage || !gtag || !orderId) return;
    const { shipping_tier, ...rest } = gtag;
    const payload = {
      ...rest,
      affiliation: getAffiliation(),
      transaction_id: orderId,
      shipping: gtag.shipping,
    };
    gtagEvent({
      action: 'purchase',
      data: payload,
    });
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        ...payload,
        event: 'purchase',
      });
    }
    dispatch(
      updateGtagData({
        affiliation: getAffiliation(),
        transaction_id: orderId,
      }),
    );
    dispatch(setTrackedPaySuccessPage());
  };
  return {
    handleTrack,
  };
};

export const useTrackPassengerPage = () => {
  const { gtag, isTrackedPassengerPage } = useAppSelector(
    (state) => state.gtagSlice,
  );
  const dispatch = useAppDispatch();
  const handleTrack = () => {
    if (isTrackedPassengerPage) return;

    gtagEvent({
      action: 'begin_checkout',
      data: gtag,
    });
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        ...gtag,
        event: 'begin_checkout',
      });
    }
    dispatch(setTrackedPassengerPage());
  };
  return {
    handleTrack,
  };
};
